/* eslint-disable complexity */
import JwtDecode from 'jwt-decode';
import {
  Configuration,
  IPublicClientApplication,
  createStandardPublicClientApplication,
} from '@azure/msal-browser-1p';
import logger from '../common/logging/AILogger';
import { ErrorCodes, LogSeverityLevel, CommonLoggingConstants } from '../config/loggingCodes';

const defaultConfig = {
  instance: '',
  authority: '',
  tenant: '',
  signInPolicy: '',
  resetPolicy: '',
  applicationId: '',
  cacheLocation: 'sessionStorage' as 'sessionStorage' | 'localStorage' | 'memoryStorage',
  scopes: [] as string[],
  webApiAcopes: [] as string[],
  redirectUri: '',
  postLogoutRedirectUri: '',
  validateAuthority: false,
  silentLoginOnly: false,
};
type AuthConfig = typeof defaultConfig;

const state: any = {
  launchApp: null,
  errorApp: null,
  idToken: null,
  accessToken: null,
  scopes: null,
  userName: '',
  webApiAccessToken: null,
  account: null
};
let appConfig: AuthConfig = { ...defaultConfig };

// let msalInstance: any;
let msalInstance: IPublicClientApplication;
let msalReady: Promise<IPublicClientApplication> | null = null;

/**
 * Flatten an MSAL / B2C failure into a single displayable string.
 * B2C returns its payload in error_description, which MSAL surfaces as errorMessage.
 */
function describeAuthError(error: any): string {
  if (!error) return 'Unknown authentication error.';
  return error.errorMessage || error.message || error.toString();
}

/**
 * Terminal auth failure: log it, clear the stuck interaction flag so a retry is
 * possible, and hand the error to the host app for rendering.
 */
function handleAuthFailure(error: any, source: string) {
  const errorText = describeAuthError(error);

  logger.logError(
    ErrorCodes.ER_01,
    errorText,
    `${CommonLoggingConstants.ComponentName}${source}`,
    error?.stack,
    LogSeverityLevel.Error
  );

  // Without this a retry throws interaction_in_progress.
  window.sessionStorage.removeItem('msal.interaction.status');

  if (state.errorApp) {
    state.errorApp(errorText);
  } else {
    console.error(errorText);
  }
}

// acquire a token silently
function getToken(accessTokenRequest) {
  return msalInstance.acquireTokenSilent(accessTokenRequest).then((accessToken) => {
    state.accessToken = accessToken;
    state.webApiAccessToken = accessToken.accessToken;
    if (state.webApiAccessToken && state.accessToken && state.launchApp) {
      state.launchApp();
    }
  }).catch((error) => {
    if (error.errorCode.indexOf('consent_required') > -1
      || error.errorCode.indexOf('interaction_required') > -1
      || error.errorCode.indexOf('login_required') > -1
      || error.errorCode.indexOf('token_renewal_error') > -1
    ) {
      msalInstance.acquireTokenRedirect(accessTokenRequest);
    } else {
      handleAuthFailure(error, 'msalAuthenticator:getToken');
    }
  });
}

const authentication = {
  initialize: (config: AuthConfig) => {
    appConfig = config;
    state.scopes = config.scopes;
    state.webApiAcopes = config.webApiAcopes;

    const msalConfig: Configuration = {
      auth: {
        clientId: config.applicationId,
        redirectUri: config.redirectUri,
        authority: `${config.instance}${config.tenant}/${config.signInPolicy}`,
        knownAuthorities: [config.authority]
      },
      cache: {
        cacheLocation: config.cacheLocation,
      },
    };
    // v5 recommended factory: returns an already-initialized PublicClientApplication.
    msalReady = createStandardPublicClientApplication(msalConfig).then((pca) => {
      msalInstance = pca;
      return pca;
    });
  },
  run: (launchApp: any, errorApp?: any) => {
    state.launchApp = launchApp;
    if (errorApp) state.errorApp = errorApp;

    if (!msalReady) {
      console.error('msalAuthenticator.run() called before initialize()');
      return;
    }

    msalReady.then(() => {
      msalInstance.handleRedirectPromise({ navigateToLoginRequestUrl: false }).then((response) => {
        if (response !== null) {
          if (response?.idTokenClaims && (response?.idTokenClaims as any)?.acr === appConfig.resetPolicy) {
            msalInstance.logoutRedirect();
          } else {
            state.account = response.account;
            getToken({ scopes: appConfig.webApiAcopes, account: response.account });
          }
        } else {
          // clear any previously stored interaction status
          window.sessionStorage.removeItem('msal.interaction.status')
          msalInstance.loginRedirect({ scopes: appConfig.scopes });
        }
      }).catch((error) => {
        // In case of password reset flow
        if (error.toString().indexOf('AADB2C90118') > -1) {
          const instance = appConfig.instance ? appConfig.instance : 'https://login.microsoftonline.com/tfp/';
          const authority = `${instance}${appConfig.tenant}/${appConfig.resetPolicy}`;
          msalInstance.loginRedirect({ scopes: appConfig.scopes, authority: authority });
          // msalInstance.loginRedirect({ scopes: appConfig.scopes });
        } else if (error.toString().indexOf('AADB2C90091') > -1) {
          // User cancelled out of the B2C page - send them back to sign-in, not an error screen
          window.sessionStorage.removeItem('msal.interaction.status');
          msalInstance.loginRedirect({ scopes: appConfig.scopes });
        } else {
          handleAuthFailure(error, 'msalAuthenticator:handleRedirectPromise');
        }
      });
    });
  },
  signOut: () => {
    msalInstance.logoutRedirect();
  },
  getAccessToken: () => state.accessToken,
  getIdToken: () => state.accessToken.idToken,
  getWebApiAccessToken: () => state.webApiAccessToken,
  getUserEmail: () => {
    const accessClaims = JwtDecode(state.accessToken.idToken);
    const guestEmailId = (accessClaims && accessClaims.email) ? accessClaims.email : '';
    return guestEmailId;
  }
};

export default authentication;
